import styles from './HATVABOTable.module.scss';
import Icon from '../../../common/Icon/Icon';
import Checkbox from '../../../common/CustomCheckbox/CustomCheckbox';
import { useAppSelector } from '../../../../redux/hooks';
import { RootState } from '../../../../redux/store';
import { useTranslation } from 'react-i18next';
import {
  getTrackingAwardIconFromCode,
  getAwardNameFromCode,
  getTrackingAwardFromCode,
  getTrackingAwardColorFromCode,
} from '../../../../utils/awardIconMapping';
import {
  HATV_APPROVAL_ACTION,
  HATV_Q_MONTH_APPROVAL_STATUS,
} from '../../../../constants/dataConstants';
import ViewRoutingModalWrapper from '../../ViewRoutingModal/ViewRoutingModal';
import { useState } from 'react';
import Modal from '../../../common/Modal/Modal';
import HATVQualificationActionModal, {
  IHATVQualificationActionProps,
} from '../../HATVQualificationActionModal/HATVQualificationActionModal';
import HATVApprovalActionModal, {
  IApproveModalProps,
} from '../../HATVApprovalAction/HATVApprovalActionModal';
import { sortTableListData, useABOProfileNavigation } from '../../../../utils/common';
import { useMonthOptions } from '../../../Context/MonthYearOptionsContext';
import { FormatLocalizedNumber } from '../../../../utils/NumberFormatter';
import { toTitleCase } from '../../../../utils/TextFormatter';

interface HATVABOTableProps {
  data: TableRowData[];
  isAwardApproval?: boolean;
  isAwardApprovalLoading?: boolean;
  setIsStatusUpdate?: (apiStatus: string) => void;
  type?: string;
  selectedRows?: TableRowData[];
  onRowSelection?: (rows: TableRowData[]) => void;
  isBulkSelectionMode?: boolean;
  selectedBulkAction?: string | null;
}

const HATVABOTable: React.FC<HATVABOTableProps> = ({
  data,
  isAwardApproval,
  isAwardApprovalLoading,
  setIsStatusUpdate,
  type,
  selectedRows,
  onRowSelection,
  isBulkSelectionMode,
  selectedBulkAction,
}) => {
  const { t } = useTranslation();
  const currentTheme = useAppSelector((state: RootState) => state.theme.currentTheme);
  const userDetails = useAppSelector((state: any) => state.userDetails.user);
  const [userAffiliateCode] = useState(userDetails?.userAffiliateCode || 0);
  const isExpanded = useAppSelector((state) => state.sideNavExpansion.isExpanded);

  const selectedSortackgroundColor = currentTheme === 'light' ? '#CACACA' : '#494949';
  const ViewApprovalModalStyles = {
    minWidth: '472px',
    minHeight: '400px',
    overflow: 'visible',
  };

  const { selectedYearMonth } = useMonthOptions();

  const [approvalModalState, setApprovalModalState] = useState<Omit<
    IApproveModalProps,
    'onClose'
  > | null>(null);

  const [qMonthModalState, setQMonthModalState] = useState<Omit<
    IHATVQualificationActionProps,
    'onClose'
  > | null>(null);

  const handleCheckboxChange = (aboItem: TableRowData) => {
    if (!onRowSelection || !selectedRows) return;
    const isSelected = selectedRows.some((row) => row.aboNumber === aboItem.aboNumber);
    if (isSelected) {
      onRowSelection(selectedRows.filter((row) => row.aboNumber !== aboItem.aboNumber));
    } else {
      onRowSelection([...selectedRows, aboItem]);
    }
  };

  const tableHeaders = [
    { columnName: t('hatv_table_header.ABO_Info'), path: 'name' },
    {
      columnName: t('hatv_table_header.Tracking/Current_Award'),
      path: 'currentAward.awardCode',
      numeric: true,
    },
    {
      columnName: t('hatv_table_header.Upline_Diamond'),
      path: 'uplineDiamond',
    },
    {
      columnName: t('hatv_table_header.Upline_Platinum'),
      path: 'uplinePlatinum',
    },
    {
      columnName: t('hatv_table_header.Account_Manager'),
      path: 'accountManager',
    },
  ];

  const awardApprovalHeaders = [
    { columnName: t('Account_table_headers.Q_Months'), path: 'qMonth' },
    { columnName: t('Account_table_headers.Q_Legs'), path: 'qLeg' },
  ];

  const renderSortIcon = (path: string) => {
    if (sortConfig.path !== path || sortConfig.order === 'nosort') {
      return (
        <Icon
          name="arrows_up_down"
          size={16}
          className={styles.sort_icon}
          color={currentTheme === 'light' ? '#2C2C2C' : '#D8D8D9'}
        />
      );
    }

    if (sortConfig.order === 'asc') {
      return (
        <Icon
          name="arrows_up"
          size={14}
          className={styles.sort_icon}
          color={currentTheme === 'light' ? '#2C2C2C' : '#D8D8D9'}
        />
      );
    }

    if (sortConfig.order === 'desc') {
      return (
        <Icon
          name="arrows_down"
          size={14}
          className={styles.sort_icon}
          color={currentTheme === 'light' ? '#2C2C2C' : '#D8D8D9'}
        />
      );
    }

    return null;
  };

  const [sortConfig, setSortConfig] = useState<{
    path: string;
    order: SortOrder;
    numeric?: boolean;
  }>({
    path: '',
    order: 'nosort',
  });
  const handleSort = (path: string, numeric?: boolean) => {
    setSortConfig((prev) => {
      const nextOrder =
        prev.path === path
          ? prev.order === 'nosort'
            ? 'asc'
            : prev.order === 'asc'
              ? 'desc'
              : 'nosort'
          : 'asc';
      return { path, order: nextOrder, numeric };
    });
  };

  const sortedData = sortTableListData(
    data,
    sortConfig.path,
    sortConfig.order,
    sortConfig?.numeric
  );

  const handleApprovalActionClick = (aboItem: TableRowData, approvalStatus: number) => {
    const {
      aboNumber,
      affiliateCode,
      name,
      status,
      roleIndicators,
      currentAward: { awardName, awardCode },
      trackingAward,
    } = aboItem;

    if (!roleIndicators) throw new Error('RoleIndicators not available!');
    setApprovalModalState({
      aboNumber,
      affiliateCode,
      approvedFor: name,
      aboHatvAwardApproverIdToApprove: roleIndicators.aboHatvAwardApproverIdToApprove,
      aboHatvAwardApproverIdToDeny: roleIndicators.aboHatvAwardApproverIdToDeny,
      approvalType: approvalStatus,
      currentAward: awardName,
      currentStatus: status,
      currentAwardCode: awardCode,
      awardQualifiedMonth: trackingAward?.awardQualifiedMonth || '',
    });
  };

  const handleQualificationActionClick = (aboItem: TableRowData, approvalStatus: number) => {
    const {
      aboNumber,
      affiliateCode,
      name,
      status,
      currentAward: { awardName, awardCode },
    } = aboItem;

    setQMonthModalState({
      aboNumber,
      affiliateCode,
      approvedFor: name,
      approvalType: approvalStatus,
      qualificationStatus: status,
      closingMonth: selectedYearMonth ? +selectedYearMonth : undefined,
      currentAward: awardName,
      currentStatus: status,
      currentAwardCode: awardCode,
    });
  };

  const handleABOInfoClick = useABOProfileNavigation();

  const renderActions = (item: TableRowData) => {
    if (isAwardApproval)
      return (
        <>
          {item.roleIndicators?.isAuthorizedToApprove && (
            <ActionButton
              action={() => handleApprovalActionClick(item, HATV_APPROVAL_ACTION.APPROVE)}
              actionIcon="tick_icon"
              disabled={!item.roleIndicators?.isAllowToApprove}
              iconClass={styles.tick_icon}
            />
          )}
          {item.roleIndicators?.isAuthorizedToDeny && (
            <ActionButton
              action={() => handleApprovalActionClick(item, HATV_APPROVAL_ACTION.DENY)}
              actionIcon="cross_circle_icon"
              disabled={!item.roleIndicators?.isAllowToDeny}
            />
          )}
          {/* View Routing Action for Award approval screen */}
          {item.routingRoleIndicators && item.trackingAward && (
            <ViewRoutingModalWrapper
              selectedAbos={[item]}
              performanceYear={item?.trackingAward?.awardQualifiedYear || ''}
              setIsStatusUpdate={setIsStatusUpdate}
            >
              <ActionButton actionIcon="view_routing_icon" />
            </ViewRoutingModalWrapper>
          )}
        </>
      );

    return (
      <>
        {item.qMonthRoleIndicators?.isAuthorizedToUpdate && (
          <ActionButton
            action={() =>
              handleQualificationActionClick(item, HATV_Q_MONTH_APPROVAL_STATUS.APPROVE)
            }
            actionIcon="tick_icon"
            disabled={
              !item.qMonthRoleIndicators?.isAllowToUpdate ||
              item.status === HATV_Q_MONTH_APPROVAL_STATUS.APPROVE
            }
            iconClass={styles.tick_icon}
          />
        )}
        {item.qMonthRoleIndicators?.isAuthorizedToUpdate && (
          <ActionButton
            action={() => handleQualificationActionClick(item, HATV_Q_MONTH_APPROVAL_STATUS.DENY)}
            actionIcon="cross_circle_icon"
            disabled={
              !item.qMonthRoleIndicators?.isAllowToUpdate ||
              item.status === HATV_Q_MONTH_APPROVAL_STATUS.DENY
            }
          />
        )}
        {!isAwardApproval && item.qMonthRoleIndicators?.isAuthorizedToUpdate && (
          <ActionButton
            action={() =>
              handleQualificationActionClick(item, HATV_Q_MONTH_APPROVAL_STATUS.ON_HOLD)
            }
            actionIcon="routing_icon"
            iconSize={14}
            iconClass={styles.routing_icon}
            disabled={
              !item.qMonthRoleIndicators?.isAllowToUpdate ||
              item.status === HATV_Q_MONTH_APPROVAL_STATUS.ON_HOLD
            }
          />
        )}
      </>
    );
  };

  return (
    <>
      {!isAwardApprovalLoading && (
        <div
          className={`hatv_table_container ${styles.hatv_table_container} ${isAwardApproval && styles.hatv_award_approval}`}
        >
          <div
            className={`${styles.abo_table} ${isExpanded && styles.expanded_width} ${isBulkSelectionMode ? styles.with_checkbox : ''}`}
          >
            <div className={`abo_table_header ${styles.table_row_data} ${styles.abo_table_header}`}>
              {tableHeaders.map(({ columnName, path, numeric }) => {
                const isActiveSort = sortConfig.path === path && sortConfig.order !== 'nosort';
                return (
                  <div
                    key={columnName}
                    className={`tbl_head_cell ${styles.tbl_head_cell}`}
                    style={{
                      backgroundColor: isActiveSort ? selectedSortackgroundColor : '',
                    }}
                  >
                    <div className={styles.head_cell}>
                      {columnName}{' '}
                      <span onClick={() => handleSort(path, numeric)}>{renderSortIcon(path)}</span>
                    </div>
                  </div>
                );
              })}

              {!isAwardApproval ? (
                <div className={`tbl_head_cell ${styles.tbl_head_cell}`}>{t('flagged_kpis')}</div>
              ) : (
                awardApprovalHeaders.map(({ columnName, path }) => {
                  const isActiveSort = sortConfig.path === path && sortConfig.order !== 'nosort';
                  return (
                    <div
                      key={columnName}
                      className={`tbl_head_cell ${styles.tbl_head_cell}`}
                      style={{
                        backgroundColor: isActiveSort ? selectedSortackgroundColor : '',
                      }}
                    >
                      <div className={styles.head_cell}>
                        {columnName}
                        <span onClick={() => handleSort(path)}>{renderSortIcon(path)}</span>
                      </div>
                    </div>
                  );
                })
              )}
              <div className={`tbl_head_cell ${styles.tbl_head_cell}`}>{t('status')}</div>
              <div className={`tbl_head_cell ${styles.tbl_head_cell}`}>{t('actions')}</div>
            </div>

            {sortedData.map((item, i) => {
              const isDisabledForBulk =
                selectedBulkAction === 'hatvdashboard_bulk_approval'
                  ? !item.roleIndicators?.isAllowToApprove
                  : item.status !== 1;
              return (
                <div key={i} className={styles.inner_row_wrapper}>
                  <div
                    className={`table_row_data ${styles.table_row_data} ${
                      isBulkSelectionMode && isDisabledForBulk ? styles.disabled_row : ''
                    }`}
                    data-testid="hatv-abo-table-row"
                  >
                    <div
                      className={`tbl_row_data_col ${styles.tbl_row_data_col}`}
                      onClick={() => handleABOInfoClick(item.aboNumber, item.affiliateCode, type)}
                    >
                      <div className={isBulkSelectionMode ? styles.abo_info_container : ''}>
                        {isBulkSelectionMode && (
                          <div
                            className={styles.checkbox_wrapper}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {!isDisabledForBulk ? (
                              <Checkbox
                                checked={selectedRows?.some(
                                  (row) => row.aboNumber === item.aboNumber
                                )}
                                checkboxHandler={() => handleCheckboxChange(item)}
                              />
                            ) : (
                              <div className={styles.checkbox_placeholder}></div>
                            )}
                          </div>
                        )}
                        <div className={styles.abo_name_content}>
                          <div className={`abo_name ${styles.abo_name}`}>{item.name}</div>
                          <div className={styles.abo_info}>
                            <div className={`abo_num ${styles.abo_num}`}>{item.aboNumber}</div>
                            {item?.marketName && (
                              <div className={`abo_num ${styles.abo_num}`}>
                                {toTitleCase(item?.marketName)}
                              </div>
                            )}

                            {!isAwardApproval && item.flag && (
                              <div className={`flag_wrapper ${styles.flag_wrapper}`}>
                                <Icon
                                  name="flag"
                                  size={14}
                                  color={
                                    item?.flag?.toLocaleLowerCase() === 'red'
                                      ? currentTheme === 'light'
                                        ? '#D91734'
                                        : '#F16A81'
                                      : currentTheme === 'light'
                                        ? '#DA7600'
                                        : '#FED773'
                                  }
                                  className={styles.flag}
                                />
                                <div
                                  className={`${styles.flag_text}${item?.flag?.toLocaleLowerCase() === 'red' ? 'red_flag' : 'yellow_flag'}`}
                                  style={{
                                    color:
                                      item?.flag?.toLocaleLowerCase() === 'red'
                                        ? currentTheme === 'light'
                                          ? '#D91734'
                                          : '#F16A81'
                                        : currentTheme === 'light'
                                          ? '#DA7600'
                                          : '#FED773',
                                  }}
                                >
                                  {t(`hatv_kpi_flag_names.${item.flag}`)}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={`tbl_row_data_col ${styles.tbl_row_data_col}`}>
                      <div className={styles.cell_data}>
                        <div className={styles.award_wrapper}>
                          <span className={styles.award_name}>
                            {getAwardNameFromCode(item?.trackingAward?.awardCode, t)}
                          </span>
                          {
                            <div
                              className={`award_icon ${styles.awardIcon}`}
                              style={{
                                backgroundColor: currentTheme === 'light' ? '#F4F4F4' : '#1C1C1E',
                              }}
                            >
                              <span className={`award_icon_left ${styles.awardIconLeft}`}>
                                {getTrackingAwardIconFromCode(
                                  item?.trackingAward?.awardTagCode,

                                  currentTheme
                                )}
                              </span>
                              <span
                                className={`award_name ${styles.awardIconRight}`}
                                style={{
                                  color: getTrackingAwardColorFromCode(
                                    item?.trackingAward?.awardTagCode,
                                    currentTheme
                                  ),
                                }}
                              >
                                {getTrackingAwardFromCode(item?.trackingAward?.awardTagCode, t)}
                              </span>
                            </div>
                          }
                        </div>
                        <div className={styles.award_wrapper}>
                          <span className={`${styles.award_name_no_ellipsis} ${styles.full_width}`}>
                            {getAwardNameFromCode(item?.currentAward?.awardCode, t)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={`tbl_row_data_col ${styles.tbl_row_data_col}`}>
                      {item.uplineDiamond ? item.uplineDiamond : '-'}
                    </div>
                    <div className={`tbl_row_data_col ${styles.tbl_row_data_col}`}>
                      {item.uplinePlatinum ? item.uplinePlatinum : '-'}
                    </div>
                    <div className={`tbl_row_data_col ${styles.tbl_row_data_col}`}>
                      {item.manager ? item.manager : '-'}
                    </div>
                    {!isAwardApproval ? (
                      <div className={`tbl_row_data_col  ${styles.tbl_row_data_col}`}>
                        {Array.isArray(item.kpis) && item.kpis.length > 0 ? (
                          item.kpis.map((kpi: Kpi, index: number) => {
                            const color =
                              kpi?.kpiFlagName?.toLocaleLowerCase() === 'red'
                                ? currentTheme === 'light'
                                  ? '#D91734'
                                  : '#F16A81'
                                : kpi?.kpiFlagName?.toLocaleLowerCase() === 'yellow'
                                  ? currentTheme === 'light'
                                    ? '#DA7600'
                                    : '#FED773'
                                  : currentTheme === 'light'
                                    ? '#2c2c2c'
                                    : '#D8D8D9';

                            return (
                              <div key={index} className={styles.kpi_details}>
                                <div className={styles.kpi_wrapper}>
                                  <span
                                    className={styles.kpi_name}
                                    style={{
                                      color: currentTheme === 'light' ? '#707070' : '#AEAEB2',
                                    }}
                                  >
                                    {kpi.kpiName}
                                    {':'}
                                  </span>
                                  <span className={styles.value_wrapper}>
                                    <span className={styles.value} style={{ color }}>
                                      {FormatLocalizedNumber(userAffiliateCode, kpi?.kpiValue)}
                                    </span>
                                    {(kpi?.kpiFlagName?.toLocaleLowerCase() === 'yellow' ||
                                      kpi?.kpiFlagName?.toLocaleLowerCase() === 'red') && (
                                      <div className={`flag_wrapper ${styles.flag_wrapper}`}>
                                        <Icon
                                          name="flag"
                                          size={14}
                                          color={color}
                                          className={styles.flag}
                                        />
                                      </div>
                                    )}
                                  </span>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className={styles.no_data}>-</div>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className={`tbl_row_data_col  ${styles.tbl_row_data_col}`}>
                          {item.qMonth}
                        </div>
                        <div className={`tbl_row_data_col  ${styles.tbl_row_data_col}`}>
                          {item.qLeg}
                        </div>
                      </>
                    )}
                    <div className={`tbl_row_data_col ${styles.tbl_row_data_col}`}>
                      {item.statusName}
                    </div>
                    <div className={`tbl_row_data_col ${styles.tbl_row_data_col}`}>
                      <div className={styles.action_wrapper}>{renderActions(item)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
            {isAwardApproval ? (
              <Modal
                showDrawer={!!approvalModalState}
                onClose={() => setApprovalModalState(null)}
                customStyle={ViewApprovalModalStyles}
              >
                {approvalModalState && (
                  <HATVApprovalActionModal
                    {...approvalModalState}
                    onClose={() => setApprovalModalState(null)}
                    setIsStatusUpdate={setIsStatusUpdate}
                  />
                )}
              </Modal>
            ) : (
              <Modal
                showDrawer={!!qMonthModalState}
                onClose={() => setQMonthModalState(null)}
                customStyle={ViewApprovalModalStyles}
              >
                {qMonthModalState && (
                  <HATVQualificationActionModal
                    {...qMonthModalState}
                    onClose={() => setQMonthModalState(null)}
                    setIsStatusUpdate={setIsStatusUpdate}
                  />
                )}
              </Modal>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const ActionButton: React.FC<{
  action?: () => void;
  actionIcon: string;
  disabled?: boolean;
  iconSize?: number;
  iconClass?: string;
}> = ({ action, actionIcon, disabled = false, iconSize = 16, iconClass }) => {
  const currentTheme = useAppSelector((state: RootState) => state.theme.currentTheme);

  const getIconColor = (disabled: boolean) => {
    if (!disabled) return currentTheme === 'light' ? '#2c2c2c' : '#D8D8D9';
    return currentTheme === 'light' ? '#5E5E5E' : '#000';
  };

  return (
    <div
      className={`action_btn ${styles.action_btn} ${disabled && 'disabled ' + styles.disabled}`}
      onClick={!disabled ? action : undefined}
      aria-disabled={disabled}
    >
      <Icon
        name={actionIcon}
        size={iconSize}
        color={getIconColor(disabled)}
        className={iconClass}
      />
    </div>
  );
}

export default HATVABOTable;
